import Draggable from "react-draggable";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {createOptionListForSelectTag, formatBuyerSellerNames, postalPattern} from "../../../helpers/utils/Utils";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import Modal from "../../../components/common/form/Modal";
import Button from "../../../components/common/form/Button";
import SelectField from "../../../components/common/form/SelectField";
import { useGetAllStatesQuery } from "../../data/states/statesApi";
import { useGetAllCountyByStateAbbrQuery } from "../../data/counties/countiesApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { useUpdatePropertyResultsMutation } from "../ordersApi";

interface Props {
    show: boolean;
    setShow: (show: boolean) => void;
    propertyData: any;
    orderData: any;
    setPropertyData: any
}

const PropertyResponseModel: React.FC<Props> = ({ show, setShow, propertyData, orderData, setPropertyData }) => {

    const [disable, setDisable] = useState<boolean>(false);
    const [legalDescription, setLegalDescription] = useState<string>("");
    const [addressLine1, setAddressLine1] = useState<string>("");
    const [subdivisionName, setSubdivisionName] = useState<string>("");
    const [countyName, setCountyName] = useState<any>();
    const [sellerName, setSellerName] = useState<string>("");
    const [taxId, setTaxId] = useState<string>("");
    const [geoId, setGeoId] = useState<string>("");
    const [propertyId, setPropertyId] = useState<string>("");
    const [orderId, setOrderId] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [postalCode, setPostalCode] = useState<string>("");
    const [stateAbbr, setStateAbbr] = useState<any>();
    const statesApiResult = useGetAllStatesQuery("");
    const countiesApiResult = useGetAllCountyByStateAbbrQuery(stateAbbr?.value ?? skipToken);
    const [updateApi, updateApiRes] = useUpdatePropertyResultsMutation();

    useEffect(() => {
        const { state, county } = propertyData?.propertyResult?.parsedAddress || {};
        const matchedState = statesApiResult?.data?.find(
            (item: any) => item?.stateAbbreviation?.toLowerCase() === state?.toLowerCase()
        );

        if (matchedState) {
            setStateAbbr({
                label: matchedState?.stateName,
                value: matchedState?.stateAbbreviation,
            });
        }

        setCountyName({
            label: county,
            value: county
        });

        setLegalDescription(propertyData?.propertyResult?.legalDescription || "");
        setAddressLine1(propertyData?.propertyResult?.address || "");
        setSubdivisionName(propertyData?.propertyResult?.subdivisionName || "");
        setCity(propertyData?.propertyResult?.parsedAddress?.city || "");
        setPostalCode(propertyData?.propertyResult?.parsedAddress?.postalCode || "");
        setSellerName(propertyData?.propertyResult?.primaryOwner || "");
        setTaxId(propertyData?.propertyResult?.taxId || "");
        setGeoId(propertyData?.propertyResult?.geoId || "");
        setPropertyId(propertyData?.id || "");
        setOrderId(orderData?.data?.id || "");
    }, [propertyData, orderData, statesApiResult?.data]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const formData = {
            legalDescription,
            address: addressLine1,
            subdivisionName,
            parsedAddress: {
                state: stateAbbr?.value,
                county: countyName?.value,
                city,
                postalCode
            },
            primaryOwner: sellerName,
            taxId,
            geoId,
            propertyResultSource: "EDITOR"
        };

        setDisable(true);
        try {
            await updateApi({
                orderId,
                propertyId,
                propertyResult: formData
            });
        } catch (error) {
            console.error("Update failed:", error);
        } finally {
            setDisable(false);
            setShow(false)
        }
    };

    const copyFromRequest = () => {
        setLegalDescription(propertyData?.legalDescription || "");
        setAddressLine1(propertyData?.addressLine1 || "");
        setCountyName(propertyData?.countyName || "");
        setStateAbbr(propertyData?.state || "");
        setCity(propertyData?.city || "");
        setPostalCode(propertyData?.zip || "");
        setSellerName(formatBuyerSellerNames(orderData?.data?.sellerName || ""));
        setTaxId(propertyData?.taxIdentifier || "");
        setPropertyId(propertyData?.id || "");
        setOrderId(orderData?.data?.id || "");

        const { state, countyName } = propertyData || {};
        const matchedState = statesApiResult?.data?.find(
            (item: any) => item?.stateAbbreviation?.toLowerCase() === state?.toLowerCase()
        );

        if (matchedState) {
            setStateAbbr({
                label: matchedState?.stateName,
                value: matchedState?.stateAbbreviation,
            });
        }

        if (countyName) {
            setCountyName({
                label: countyName,
                value: countyName
            });
        }
    };


    return (
        <Modal
            title="Edit Property Response"
            show={show}
            setShow={setShow}
            className="w-2/5 bg-text-0 rounded-lg shadow-lg"
            fields={
                <Form onSubmit={handleSubmit} fields={
                    <>
                        <div className="modal-body  mb-3 p-3">
                            <InputField
                                label="Tax ID"
                                name="taxId"
                                value={taxId}
                                onChange={(e) => setTaxId(e.target.value)}
                                placeholder="Enter Tax ID"
                            />
                            <InputField
                                label="Geo Id"
                                name="geoId"
                                value={geoId}
                                onChange={(e) => setGeoId(e.target.value)}
                                placeholder="Enter Geo Id"
                            />
                            <InputField
                                label="Owner"
                                name="owner"
                                value={sellerName}
                                onChange={(e) => setSellerName(e.target.value)}
                                placeholder="Enter Owner"
                            />
                            <InputField
                                label="Address"
                                name="address"
                                value={addressLine1}
                                onChange={(e) => setAddressLine1(e.target.value)}
                                placeholder="Enter Address"
                                required
                            />
                            <InputField
                                label="Legal Description"
                                name="legalDescription"
                                value={legalDescription}
                                onChange={(e) => setLegalDescription(e.target.value)}
                                placeholder="Enter Legal Description"
                            />
                            <SelectField
                                placeholder='Select State...'
                                value={stateAbbr}
                                options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                                onChange={(selectedOption) => {
                                    setCountyName(null);
                                    setStateAbbr(selectedOption);
                                }}
                                label={"State"}
                                required
                            />

                            <SelectField
                                placeholder='Select County...'
                                value={countyName}
                                options={createOptionListForSelectTag(countiesApiResult?.data, "countyName", "id")}
                                onChange={(selectedOption) => {
                                    setCountyName(selectedOption)
                                }}
                                label={"County"}
                                required
                                isDisabled={!stateAbbr}
                            />

                            <InputField
                                label="City"
                                name="city"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="Enter City"

                            />
                            <InputField
                                label="Postal Code"
                                name="postalCode"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                pattern={postalPattern}
                                placeholder="Enter Postal Code"

                            />
                            <InputField
                                label="Subdivision"
                                name="subdivisionName"
                                value={subdivisionName}
                                onChange={(e) => setSubdivisionName(e.target.value)}
                                placeholder="Enter Subdivision"

                            />
                        </div>
                        <div className={"mt-4 border-t border-t-surface-5 flex justify-end"}>
                            <ButtonTray
                                buttons={[
                                    {
                                        buttonProps: {
                                            btnText: "Copy Request",
                                            onClick: () => copyFromRequest(),
                                            type: "copy",
                                            isDisabled: disable,
                                        },
                                        buttonType: "button",
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Cancel",
                                            onClick: () => setShow(false),
                                            type: "cancel",
                                            isDisabled: disable,
                                        },
                                        buttonType: "button",
                                    },
                                    {
                                        buttonProps: {
                                            btnText: "Submit",
                                            type: "submit",
                                            isLoading: updateApiRes.isLoading,
                                        },
                                        buttonType: "button",
                                    },
                                ]}
                                align="center"
                                gap={2}
                            />
                        </div>


                    </>
                }

                />

            }
        />


    );
};

export default PropertyResponseModel;